import React from 'react';
import { useFormik } from "formik";
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';

function Checkout() {
  const location = useLocation();
  const cart = location?.state?.data;
  const reservations = cart?.reservations || [];
  const orders = cart?.orders || [];
  const user = cart?.User || {};

  const DateDifference = (start_date, end_date) => {
    const startDate = new Date(start_date);
    const endDate = new Date(end_date);
    const diffInMilliseconds = Math.abs(endDate - startDate);
    return Math.max(1, Math.ceil(diffInMilliseconds / (1000 * 60 * 60 * 24)));
  };

  const totalOrderCost = orders.reduce((sum, order) => sum + Number(order.total_price || order.food?.price * order.foods_number || 0), 0);
  const totalReservCost = reservations.reduce((sum, reserv) => sum + Number(reserv.total_price || DateDifference(reserv.entry_date, reserv.exit_date) * reserv.room?.price || 0), 0);
  const total = totalOrderCost + totalReservCost;
  const taxes = total * 0.05;

  const form = useFormik({
    initialValues: {
      name: user?.name || '',
      family: user?.family || '',
      mobile_number: user?.mobile_number || '',
      email: user?.email || '',
      password: '',
      repassword: '',
      saveInfo: false,
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (values.password !== values.repassword) {
        Swal.fire({ icon: 'warning', text: 'Passwords do not match.', confirmButtonText: 'OK', customClass: { popup: 'sweetalert2-custom-popup' } });
        setSubmitting(false);
        return;
      }

      Swal.fire({ icon: 'success', text: "Checkout information received", timer: 1600, showConfirmButton: false, customClass: { popup: 'sweetalert2-custom-popup' } });
      if (values.saveInfo) {
        localStorage.setItem('checkout_profile', JSON.stringify({
          name: values.name,
          family: values.family,
          mobile_number: values.mobile_number,
          email: values.email,
        }));
      }
      resetForm();
      setTimeout(() => setSubmitting(false), 300);
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) errors.name = 'Name is required';
      if (!values.family) errors.family = 'Family name is required';
      if (!values.mobile_number) errors.mobile_number = 'Mobile number is required';
      if (!values.email) errors.email = 'Email is required';
      if (!values.password) errors.password = 'Password is required';
      if (!values.repassword) errors.repassword = 'Please confirm your password';
      if (values.password !== values.repassword) errors.repassword = 'Passwords do not match';
      return errors;
    }
  });

  const fieldClass = (name) => `field ${form.errors[name] && form.touched[name] ? 'border-red-300 ring-4 ring-red-50' : ''}`;

  if (!cart) {
    return (
      <section className="bg-surface py-20">
        <div className="section-shell">
          <div className="premium-card p-10 text-center">
            <h1 className="section-title">No checkout details found</h1>
            <p className="section-copy mx-auto">Please review your cart before continuing to checkout.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-surface py-12 sm:py-16">
      <div className="section-shell grid gap-8 xl:grid-cols-[1fr_24rem]">
        <div className="space-y-6">
          <div className="premium-card bg-white p-6 sm:p-8">
            <span className="eyebrow">Secure checkout</span>
            <h1 className="section-title mt-5">Confirm your trip</h1>
            <p className="section-copy">Review your customer information and payment credentials before completing the booking.</p>

            <form onSubmit={form.handleSubmit} className="mt-8 grid gap-5 sm:grid-cols-2">
              <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Name</span><input className={fieldClass('name')} name="name" value={form.values.name} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
              <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Family</span><input className={fieldClass('family')} name="family" value={form.values.family} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
              <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Mobile</span><input className={fieldClass('mobile_number')} name="mobile_number" value={form.values.mobile_number} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
              <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Email</span><input className={fieldClass('email')} name="email" type="email" value={form.values.email} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
              <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Password</span><input className={fieldClass('password')} name="password" type="password" value={form.values.password} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
              <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Confirm password</span><input className={fieldClass('repassword')} name="repassword" type="password" value={form.values.repassword} onChange={form.handleChange} onBlur={form.handleBlur} /></label>

              <div className="mt-4 space-y-2 text-sm text-red-500 sm:col-span-2">
                {form.errors.name && form.touched.name && <p>{form.errors.name}</p>}
                {form.errors.family && form.touched.family && <p>{form.errors.family}</p>}
                {form.errors.mobile_number && form.touched.mobile_number && <p>{form.errors.mobile_number}</p>}
                {form.errors.email && form.touched.email && <p>{form.errors.email}</p>}
                {form.errors.password && form.touched.password && <p>{form.errors.password}</p>}
                {form.errors.repassword && form.touched.repassword && <p>{form.errors.repassword}</p>}
              </div>

              <label className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4 text-sm font-bold text-ink sm:col-span-2">
                <input type="checkbox" name="saveInfo" checked={form.values.saveInfo} onChange={form.handleChange} className="h-5 w-5 rounded border-line text-gotrip-700 focus:ring-gotrip-500" />
                Save this information for next time
              </label>

              <div className="flex justify-end sm:col-span-2">
                <button type="submit" disabled={form.isSubmitting} className="btn-primary">{form.isSubmitting ? 'Processing...' : 'Complete checkout'}</button>
              </div>
            </form>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="premium-card bg-white p-6">
            <h2 className="text-xl font-black tracking-tight text-ink">Reservation summary</h2>
            <div className="mt-5 space-y-4">
              {reservations.map((reservation) => (
                <div key={reservation.id} className="rounded-3xl bg-slate-50 p-4">
                  <p className="font-black text-ink">{reservation.room?.room_name}</p>
                  <p className="mt-1 text-sm text-muted">{reservation.entry_date} → {reservation.exit_date}</p>
                  <div className="mt-3 flex justify-between text-sm font-bold"><span>Guests</span><span>{reservation.persons_number}</span></div>
                  <div className="mt-1 flex justify-between text-sm font-bold"><span>Total</span><span>${DateDifference(reservation.entry_date, reservation.exit_date) * reservation.room?.price}</span></div>
                </div>
              ))}
            </div>
          </div>

          <div className="premium-card bg-white p-6">
            <h2 className="text-xl font-black tracking-tight text-ink">Dining summary</h2>
            <div className="mt-5 space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="rounded-3xl bg-slate-50 p-4">
                  <p className="font-black text-ink">{order.food?.name}</p>
                  <div className="mt-3 flex justify-between text-sm font-bold"><span>Quantity</span><span>{order.foods_number}</span></div>
                  <div className="mt-1 flex justify-between text-sm font-bold"><span>Total</span><span>${order.food?.price * order.foods_number}</span></div>
                </div>
              ))}
            </div>
          </div>

          <div className="premium-card bg-slate-950 p-6 text-white">
            <h2 className="text-xl font-black tracking-tight">Order total</h2>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between text-slate-300"><span>Subtotal</span><span>${total}</span></div>
              <div className="flex justify-between text-slate-300"><span>Taxes</span><span>${taxes}</span></div>
              <div className="border-t border-white/10 pt-4 text-2xl font-black"><div className="flex justify-between"><span>Total</span><span>${total + taxes}</span></div></div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default Checkout;
